// HeroSection Component - Main hero section for the homepage
// This component displays the main hero banner with parallax background, animated text,
// and call-to-action button. It includes smooth scroll effects and interactive elements.
//
// USAGE LOCATIONS:
// - src/pages/HomePage.jsx (main landing page hero section - only used on homepage)
//
// The component is the primary visual element on the homepage and includes:
// - Parallax background image with scroll effects
// - Animated text that fades and moves on scroll
// - Interactive logo that opens an info modal
// - Call-to-action button that scrolls to categories section
// - Responsive design for mobile, tablet, and desktop

import { useState, useCallback, memo } from 'react';
import { Link } from 'react-router-dom';
import InfoModal from '../Modal/InfoModal';
import { motion, useScroll, useTransform } from 'framer-motion';
import { HeroActionButton } from '../UI/Button';

// Animation constants
const SCROLL_RANGES = {
  BACKGROUND: [0, 500],
  TEXT: [0, 300]
};

const ANIMATION_VALUES = {
  BACKGROUND_Y: ['0%', '30%'],
  TEXT_OPACITY: [1, 0],
  TEXT_Y: [0, -50]
};

const HeroSection = () => {
  // State for controlling the info modal visibility
  const [isOpen, setIsOpen] = useState(false);
  // Framer Motion scroll hook for scroll-based animations
  const { scrollY } = useScroll();
  
  // Parallax effect for the background image - moves slower than scroll
  const backgroundY = useTransform(scrollY, SCROLL_RANGES.BACKGROUND, ANIMATION_VALUES.BACKGROUND_Y);
  
  // Text fade and move effects when scrolling down
  const textOpacity = useTransform(scrollY, SCROLL_RANGES.TEXT, ANIMATION_VALUES.TEXT_OPACITY); // Fades out
  const textY = useTransform(scrollY, SCROLL_RANGES.TEXT, ANIMATION_VALUES.TEXT_Y); // Moves up

  // Callback function to toggle the info modal popup
  const togglePopup = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  return (
    <>
      {/* Parallax background image with scroll-based movement */}
      <motion.div 
        className="absolute top-0 left-0 w-full h-screen z-0 will-change-transform overflow-hidden"
        style={{
          y: backgroundY, // Parallax effect - moves slower than scroll
        }}
      >
        {/* Background image with fetchpriority for LCP optimization */}
        <img
          src="./img/main-hero.webp"
          alt=""
          fetchpriority="high"
          className="absolute top-0 left-0 w-full h-full object-cover"
          style={{ backfaceVisibility: 'hidden' }}
        />
        {/* Dark overlay for better text readability */}
        <div className="absolute top-0 left-0 w-full h-full bg-black/30 z-10"></div>
      </motion.div>

      {/* Main hero header container - full screen height */}
      <header className="min-h-screen flex flex-col">
        {/* Top section with logo and text content */}
        <div className="pt-8 pb-4 static text-gray-50 z-1 shadow-[0_2px_4px_rgba(0,0,0,0.3)]">
          <div className="max-w-7xl mx-auto px-3 sm:px-5 flex flex-col md:flex-row items-center md:items-start justify-between gap-3 sm:gap-5 static">
            {/* Logo section - clickable and animated */}
            <div className="flex-[0.8] max-w-[200px] sm:max-w-[250px] md:max-w-[450px] relative md:top-[-40px]">
              <motion.div 
                onClick={togglePopup} // Opens info modal when clicked
                className="cursor-pointer"
                whileHover={{ scale: 1.05 }} // Hover scale effect
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <img 
                  src="/img/logo.png" 
                  alt="Autyzm od kuchni"
                  width="493"
                  height="493"
                  className="w-full h-auto block relative z-1 scale-125 sm:scale-100" 
                />
              </motion.div>
            </div>
            
            {/* Hero text content - main headline and description */}
            <div className="flex-2 group relative z-10 mt-4 md:mt-0">
              <div className="max-w-3xl mx-auto py-4 sm:py-6 px-4 sm:px-8 md:px-16 relative">
                {/* Subtle background gradient that appears on hover */}
                <div className="absolute inset-0 bg-gradient-radial from-black/10 via-transparent to-transparent transition-all duration-300 group-hover:from-black/20"></div>
                {/* Animated text container with scroll effects */}
                <motion.div 
                  className="relative transform transition-all duration-300 group-hover:translate-y-[-8px]"
                  style={{ opacity: textOpacity, y: textY }} // Scroll-based fade and movement
                >
                  {/* Main headline - responsive typography */}
                  <h1 className="font-['Caveat'] text-xl sm:text-2xl md:text-4xl font-bold text-white leading-relaxed text-center tracking-wide mb-4 sm:mb-6 transition-all duration-300 group-hover:text-gray-50 drop-shadow-lg group-hover:drop-shadow-xl" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                    Dieta Eliminacyjna <br/>
                    <span className="text-lg sm:text-xl md:text-3xl font-normal tracking-wider opacity-95 transition-all duration-300 group-hover:opacity-100">
                      Transformująca <br/>
                      Funkcjonowanie <br/>
                      W Autyzmie I Zaburzeniach <br className="hidden sm:block" />
                      <span className="hidden sm:inline">Neurorozwojowych</span>
                      <span className="sm:hidden text-[1.0em]">Neurorozwojowych</span>
                    </span>
                  </h1>
                  {/* Subtitle - hidden on mobile, visible on larger screens */}
                  <h2 className="hidden sm:block mt-4 sm:mt-6 font-['Patrick_Hand'] text-base sm:text-lg md:text-xl font-normal text-gray-50/90 leading-relaxed text-center tracking-wide transition-all duration-300 group-hover:text-white" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                    Proste, odżywcze przepisy bez glutenu, nabiału krowiego, cukru i <br className="hidden sm:block" />
                    bez smażenia – wspierające zdrowie i rozwój
                  </h2>
                </motion.div>
              </div>
            </div>

            {/* Spacer div for desktop layout balance */}
            <div className="hidden md:block flex-[0.8]"></div>
          </div>
        </div>

        {/* Spacer section to push content to bottom */}
        <div className="flex justify-center items-end flex-grow">
          {/* Empty space above the transition */}
        </div>
        
        {/* Bottom transition section with gradient and call-to-action */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden">
          {/* Enhanced gradient overlay with text integration */}
          <div className="w-full h-48 sm:h-56 md:h-64 bg-gradient-to-b from-transparent via-[#2D3748]/60 to-[#2D3748]/95">
            {/* Integrated banner text - tagline */}
            <div className="hidden sm:block absolute bottom-[120px] sm:bottom-[130px] md:bottom-[150px] w-full text-center">
              <div className="transform scale-125 origin-center">
                <p className="text-white/80 text-xs sm:text-base md:text-lg font-['Patrick_Hand'] tracking-wide px-4">
                  KUCHNIA, KTÓRA ODŻYWIA: PRZEPISY WSPIERAJĄCE W AUTYŹMIE
                </p>
                {/* Subtle divider line */}
                <div className="w-16 sm:w-20 md:w-24 h-[1px] bg-green-300/50 mx-auto mt-2"></div>
              </div>
            </div>
          </div>

          {/* Call-to-action button - scrolls to categories section */}
          <div className="absolute bottom-[85px] sm:bottom-[90px] md:bottom-[100px] left-0 right-0 mx-auto flex justify-center z-30">
            <HeroActionButton 
              text="ODKRYJ PRZEPISY" 
              size="hero"
            />
          </div>

          {/* Na mobile: pasek doradztwa na hero (na img) – „Potrzebujesz wsparcia? Zobacz ofertę” */}
          <div className="md:hidden absolute bottom-0 left-0 right-0 z-20 px-4 pb-4 pt-8">
            <div className="bg-[#2D3748]/90 backdrop-blur-sm rounded-t-2xl px-4 py-3 text-center">
              <Link
                to="/uslugi"
                className="flex flex-wrap items-center justify-center gap-2 text-white/95 hover:text-white font-['Patrick_Hand'] text-sm tracking-wide transition-colors"
                aria-label="Zobacz ofertę doradztwa"
              >
                <span>Potrzebujesz indywidualnego wsparcia?</span>
                <span className="text-green-300 font-semibold flex items-center gap-1">
                  Zobacz ofertę
                  <span aria-hidden>→</span>
                </span>
              </Link>
            </div>
          </div>
          
          {/* Additional gradient for smoother transition to next section */}
          <div className="absolute bottom-8 sm:bottom-10 md:bottom-14 left-0 w-full h-16 sm:h-20 md:h-24 z-10 pointer-events-none"
            style={{
              background: 'linear-gradient(to bottom, rgba(45,55,72,0), rgba(247,250,252,0.3))'
            }}
          ></div>

          {/* Smooth curved transition to next section */}
          <div className="absolute bottom-0 left-0 w-full h-10 sm:h-12 md:h-16 overflow-hidden">
            {/* Gradient overlay for smooth color transition */}
            <div className="absolute bottom-0 left-0 w-full h-36 z-10 pointer-events-none"
               style={{
                 background: 'linear-gradient(to top, rgba(247,250,252,1), rgba(247,250,252,0.6), rgba(247,250,252,0))'
               }}
            ></div>
            {/* Curved white section that connects to the next content area */}
            <div 
              className="w-[150%] h-10 sm:h-12 md:h-16 bg-[#F7FAFC] absolute -left-[25%]"
              style={{
                borderTopLeftRadius: '100%',
                borderTopRightRadius: '100%',
                boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.05)',
                backgroundImage: 'linear-gradient(to bottom, rgba(247,250,252,0.7), rgba(247,250,252,1))'
              }}
            ></div>
          </div>
        </div>
      </header>

      {/* Info modal that opens when logo is clicked */}
      <InfoModal isOpen={isOpen} togglePopup={togglePopup} />
    </>
  );
};

export default memo(HeroSection);